import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, GeoJSON, Rectangle } from "react-leaflet";
import type { Layer, PathOptions } from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  TILE_URL,
  TILE_LABELS_URL,
  TYPE_COLORS,
  RISK_COLORS,
  buildPushpinIcon,
  ensureMapStyles,
  FitBounds,
  InvalidateSizeOnMount,
  KENYA_BOUNDS,
} from "./mapShared";

/** Known Kenya ports of entry with approximate coordinates + transport mode. */
export const PORT_COORDS: Record<
  string,
  { lat: number; lng: number; type: "Air" | "Sea" | "Land" }
> = {
  jkia: { lat: -1.3192, lng: 36.9278, type: "Air" },
  "jomo kenyatta": { lat: -1.3192, lng: 36.9278, type: "Air" },
  nairobi: { lat: -1.3192, lng: 36.9278, type: "Air" },
  "wilson airport": { lat: -1.3206, lng: 36.8148, type: "Air" },
  mombasa: { lat: -4.034, lng: 39.5942, type: "Sea" },
  "mombasa port": { lat: -4.034, lng: 39.5942, type: "Sea" },
  kilindini: { lat: -4.05, lng: 39.65, type: "Sea" },
  malaba: { lat: 0.6347, lng: 34.2756, type: "Land" },
  "malaba border": { lat: 0.6347, lng: 34.2756, type: "Land" },
  busia: { lat: 0.46, lng: 34.12, type: "Land" },
  namanga: { lat: -2.55, lng: 36.7833, type: "Land" },
  loitokitok: { lat: -2.9167, lng: 37.5167, type: "Land" },
  taveta: { lat: -3.4, lng: 37.6833, type: "Land" },
  isebania: { lat: -1.2333, lng: 34.4833, type: "Land" },
  moyale: { lat: 3.527, lng: 39.056, type: "Land" },
  liboi: { lat: 0.35, lng: 40.8667, type: "Land" },
  "eldoret airport": { lat: 0.4044, lng: 35.2389, type: "Air" },
  kisumu: { lat: -0.0861, lng: 34.7289, type: "Air" },
};

const BORDERS_URL =
  "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson";

let cachedBorders: any = null;

type PortPoint = {
  port?: string;
  entry_type?: string;
  screened?: number;
  high_risk?: number;
  latitude?: number | null;
  longitude?: number | null;
};

type Colors = {
  air?: string;
  sea?: string;
  land?: string;
  tooltipBg: string;
  tooltipText: string;
  tooltipTextMuted: string;
};

type Props = {
  mode?: "light" | "dark";
  ports?: PortPoint[];
  colors: Colors;
};

const resolveType = (entryType?: string, fallback?: "Air" | "Sea" | "Land"): "Air" | "Sea" | "Land" => {
  const t = (entryType || fallback || "").toLowerCase();
  if (t.startsWith("air")) return "Air";
  if (t.startsWith("sea") || t.startsWith("ship")) return "Sea";
  return "Land";
};

const resolvePort = (name?: string, entryType?: string) => {
  const key = (name || "").toLowerCase().trim();
  const hit = Object.entries(PORT_COORDS).find(
    ([k]) => key.includes(k) || k.includes(key)
  );
  if (hit) {
    return {
      ...hit[1],
      type: resolveType(entryType, hit[1].type),
      label: name || hit[0],
    };
  }
  return {
    lat: 0.0236,
    lng: 37.9062,
    type: resolveType(entryType),
    label: name || "Unknown",
  };
};

const isKenyaFeature = (props: any) => {
  const n = String(props?.name || props?.ADMIN || props?.NAME || "").toLowerCase();
  return n === "kenya" || n.includes("kenya");
};

const PortEntryMap: React.FC<Props> = ({ mode = "light", ports = [], colors }) => {
  const [borders, setBorders] = useState<any>(cachedBorders);

  useEffect(() => {
    ensureMapStyles();
    if (cachedBorders) return;
    let alive = true;
    fetch(BORDERS_URL)
      .then((r) => r.json())
      .then((g) => {
        cachedBorders = g;
        if (alive) setBorders(g);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const icons = useMemo(
    () => ({
      Air: buildPushpinIcon("Air"),
      Sea: buildPushpinIcon("Sea"),
      Land: buildPushpinIcon("Land"),
    }),
    []
  );

  const markers = useMemo(() => {
    return ports
      .map((p) => {
        // Prefer real coordinates from entry_points.latitude/longitude — only
        // fall back to the fuzzy title-matched dictionary (which defaults to
        // Kenya's center for anything it doesn't recognize) when the DB
        // hasn't been backfilled yet for this port.
        const hasDbCoords = Number.isFinite(p.latitude) && Number.isFinite(p.longitude);
        const resolved = hasDbCoords
          ? { lat: p.latitude as number, lng: p.longitude as number, type: resolveType(p.entry_type), label: p.port }
          : resolvePort(p.port, p.entry_type);
        return {
          ...resolved,
          screened: p.screened || 0,
          high_risk: p.high_risk || 0,
          port: p.port || resolved.label,
        };
      })
      .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
  }, [ports]);

  const positions = useMemo(() => {
    const pts = markers.map((m) => [m.lat, m.lng] as [number, number]);
    // Always include Kenya bbox corners so the country stays in view / highlighted frame
    pts.push(KENYA_BOUNDS[0], KENYA_BOUNDS[1]);
    return pts;
  }, [markers]);

  const kenyaOnly = useMemo(() => {
    if (!borders?.features) return null;
    const feats = borders.features.filter((f: any) => isKenyaFeature(f.properties));
    if (!feats.length) return null;
    return { type: "FeatureCollection", features: feats };
  }, [borders]);

  const kenyaStyle = (): PathOptions => ({
    fillColor: RISK_COLORS.kenya,
    fillOpacity: mode === "dark" ? 0.35 : 0.28,
    color: RISK_COLORS.kenya,
    weight: 2.5,
    opacity: 1,
  });

  const onKenya = (_: any, layer: Layer) => {
    layer.bindTooltip(
      `<strong>Kenya</strong><br/><span style="opacity:.8">Points of entry</span>`,
      { sticky: true, className: "dash-map-tip", direction: "top" }
    );
  };

  return (
    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
      <MapContainer
        center={[0.0236, 37.9062]}
        zoom={6}
        style={{
          height: "100%",
          width: "100%",
          background: mode === "dark" ? "#0a1120" : "#eef2f7",
        }}
        scrollWheelZoom={false}
        attributionControl={false}
      >
        <TileLayer url={TILE_URL[mode]} />
        <TileLayer url={TILE_LABELS_URL[mode]} />
        <InvalidateSizeOnMount />
        <FitBounds positions={positions} padding={70} maxZoom={7} />

        {/* Fallback highlight if GeoJSON not ready */}
        {!kenyaOnly && (
          <Rectangle
            bounds={KENYA_BOUNDS}
            pathOptions={{
              color: RISK_COLORS.kenya,
              weight: 2,
              fillColor: RISK_COLORS.kenya,
              fillOpacity: 0.12,
              dashArray: "4 4",
            }}
          >
            <Tooltip sticky className="dash-map-tip">
              <span>Kenya · Points of entry</span>
            </Tooltip>
          </Rectangle>
        )}

        {kenyaOnly && (
          <GeoJSON
            key={`kenya-${mode}`}
            data={kenyaOnly as any}
            style={kenyaStyle}
            onEachFeature={onKenya}
          />
        )}

        {markers.map((m, i) => (
          <Marker key={`${m.port}-${i}`} position={[m.lat, m.lng]} icon={icons[m.type]}>
            <Tooltip direction="top" offset={[0, -28]} opacity={1} className="dash-map-tip">
              <div>
                <strong>{m.port}</strong>
                <br />
                Type: {m.type}
                <br />
                Screened: {m.screened}
                {m.high_risk ? (
                  <>
                    <br />
                    High-risk: {m.high_risk}
                  </>
                ) : null}
              </div>
            </Tooltip>
            <Popup>
              <Box sx={{ minWidth: 140 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{m.port}</Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>Type: {m.type}</Typography>
                <Typography sx={{ fontSize: 11 }}>Screened: {m.screened}</Typography>
                <Typography sx={{ fontSize: 11 }}>High-risk: {m.high_risk}</Typography>
              </Box>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <Box
        sx={{
          position: "absolute",
          left: 12,
          bottom: 12,
          zIndex: 500,
          background: colors.tooltipBg,
          color: colors.tooltipText,
          px: 1.5,
          py: 1,
          borderRadius: "10px",
          boxShadow: "0 6px 20px rgba(0,0,0,.25)",
        }}
      >
        <Typography sx={{ fontSize: 9, color: colors.tooltipTextMuted, mb: 0.6, textTransform: "uppercase", letterSpacing: 0.4 }}>
          Entry type
        </Typography>
        <Box sx={{ display: "flex", gap: 1.4, alignItems: "center", flexWrap: "wrap" }}>
          {([
            { c: colors.air || TYPE_COLORS.Air, l: "Air" },
            { c: colors.sea || TYPE_COLORS.Sea, l: "Sea" },
            { c: colors.land || TYPE_COLORS.Land, l: "Land" },
            { c: RISK_COLORS.kenya, l: "Kenya" },
          ] as const).map((x) => (
            <Box key={x.l} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: x.c }} />
              <Typography sx={{ fontSize: 10, color: colors.tooltipTextMuted }}>{x.l}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PortEntryMap;
